import type { StoreSpecial as V3StoreSpecial } from '@iheartjane/data-access';
import { ALL_STORE_SPECIALS_OPTS } from '@iheartjane/data-access';
import { useStoreSpecials } from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { useNavigateBack } from '@iheartjane/menu';
import {
  Box,
  Button,
  Flex,
  Link,
  Loading,
  SearchField,
  Typography,
} from '@iheartjane/reefer';
import { pluralize } from '@iheartjane/utils';
import { useCallback, useMemo, useState } from 'react';
import { useLocation, useMatches, useParams } from 'react-router';

import { useRecommendedSort } from '@jane/dm/internal';
import {
  FilterCheckbox,
  Modal,
  ModalHeader,
  NoResults,
  SearchInputContainer,
} from '@jane/kiosk/components';
import type { RootLoaderType } from '@jane/kiosk/data-access';
import { searchOptions } from '@jane/kiosk/data-access';
import {
  useKioskSpacing,
  useRowContext,
  useTypedRouteLoaderData,
} from '@jane/kiosk/hooks';
import {
  useBrandSpecials,
  useProductSort,
  useSearchContext,
} from '@jane/kiosk/providers';
import type { OptionalMenuRouteParams } from '@jane/kiosk/routing/types';
import {
  getSource,
  isFilterSelected,
  isSupportedStoreSpecial,
  trackFilteredProductList,
} from '@jane/kiosk/util';
import type { ProductFilterKeys } from '@jane/search/data-access';
import {
  buildSearchFilter,
  facetToFilter,
  handleFilterChange,
  handleFilterDeselect,
  mapCurrentSortToSearchSort,
  updateFacetCounts,
} from '@jane/search/util';
import { useGetStoreSpecials } from '@jane/shared-ecomm/data-access';
import { useGetStore } from '@jane/shared/data-access';
import type { Store, StoreSpecial } from '@jane/shared/types';

export const TerpeneFilters = () => {
  const { store: initialStore, storeId } =
    useTypedRouteLoaderData<RootLoaderType>('root');
  const { category, specialId, brandSpecialId } =
    useParams<OptionalMenuRouteParams>();
  const { data: store } = useGetStore<Store>(storeId, {
    initialData: initialStore,
  });
  const { currentSort } = useProductSort({
    defaultSortName: null,
    optionalFilters: store.optional_filters,
    store,
  });

  const { brandSpecials, brandSpecialFilter } = useBrandSpecials();
  const useV3 = useFlag(FLAGS.c3UseV3StoreSpecials);
  const { data: v1Specials = [] } = useGetStoreSpecials(storeId, {
    enabled: !useV3 && !!storeId,
  });
  const v3SpecialsQuery = useStoreSpecials({
    ...ALL_STORE_SPECIALS_OPTS,
    enabled: !!useV3 && !!storeId,
    storeId,
  });
  const specials = useMemo<(StoreSpecial | V3StoreSpecial)[]>(() => {
    if (useV3) {
      return v3SpecialsQuery.data?.pages.flatMap((page) => page.specials) ?? [];
    }
    return v1Specials;
  }, [useV3, v3SpecialsQuery.data, v1Specials]);
  const supportedStoreSpecials = specials.filter((special) =>
    isSupportedStoreSpecial(special)
  );
  const specialInScope = supportedStoreSpecials.find(
    ({ id }) => `${id}` === specialId
  );
  const brandSpecial = brandSpecials.find(
    ({ id }) => `${id}` === brandSpecialId
  );

  const [terpeneSearchTerm, setTerpeneSearchTerm] = useState('');

  const { pathname } = useLocation();
  const goBack = useNavigateBack(`stores/${storeId}/menu`);
  const { getKioskSpacing, isIpad } = useKioskSpacing();
  const { searchState, setSearchState } = useSearchContext();

  const { featuredFilter } = useRowContext(store);
  const isGold = category === 'jane-gold';

  const options = searchOptions({
    categories: [category],
    customRowFilter: isGold ? brandSpecialFilter : featuredFilter,
    storeId,
    storeSpecial: specialInScope,
    brandSpecial,
  });

  const searchFilter = buildSearchFilter({
    searchState,
    staticFilters: options.staticFilters,
  });

  const searchFilterWithoutTerpenes = buildSearchFilter({
    searchState: {
      ...searchState,
      filters: { ...searchState.filters, compound_names: [] },
    },
    staticFilters: options.staticFilters,
  });

  const searchSort = mapCurrentSortToSearchSort(currentSort);

  const {
    searchResultFacets: initialResultsFacets,
    numHits: totalProductCount,
    isLoading: initialResultsFacetsLoading,
  } = useRecommendedSort({
    maxProducts: -1,
    searchAttributes: ['*'],
    searchFilter: searchFilterWithoutTerpenes,
    searchSort,
    storeId: store.id,
  });

  const {
    searchResultFacets: resultsFacets,
    isLoading: resultsFacetsLoading,
    numHits: filteredProductCount,
    isSuccess: resultsFacetsSuccess,
  } = useRecommendedSort({
    maxProducts: -2,
    searchAttributes: ['*'],
    searchFilter,
    searchSort,
    storeId: store.id,
  });

  const presentedFacets = useMemo(
    () => updateFacetCounts(searchState, initialResultsFacets, resultsFacets),
    [searchState, initialResultsFacets, resultsFacets]
  );

  const terpenes = facetToFilter(presentedFacets['compound_names'] ?? {}, {
    filterGroup: 'Terpenes',
  })
    .filter(
      ({ count, value }) =>
        value.toLowerCase().includes(terpeneSearchTerm.toLowerCase()) &&
        count > 0
    )
    .sort((a, b) => a.value.toLowerCase().localeCompare(b.value.toLowerCase()));

  const onChange = useCallback(
    (filterKey: ProductFilterKeys, filterValue: string) => {
      return setSearchState(
        handleFilterChange(filterKey, filterValue, searchState)
      );
    },
    [searchState, setSearchState]
  );

  const onDeselect = useCallback(
    (filterKey: ProductFilterKeys) => {
      return setSearchState(handleFilterDeselect(filterKey, searchState));
    },
    [searchState, setSearchState]
  );

  const isLoading = initialResultsFacetsLoading || resultsFacetsLoading;

  const noResults = !isLoading && terpenes.length === 0 && resultsFacetsSuccess;

  const onClearAll = () =>
    setSearchState(handleFilterDeselect('all', searchState));

  const matches = useMatches();

  const onShowFilteredProducts = useCallback(() => {
    trackFilteredProductList({
      dealName: specialInScope ? [specialInScope.title] : null,
      filterOptions: searchState,
      filteredProductCount,
      source: getSource({
        currentSpecial: specialInScope,
        isTopper: false,
        matches,
        pathname,
      }),
      totalProductCount,
    });
  }, [
    filteredProductCount,
    matches,
    pathname,
    searchState,
    specialInScope,
    totalProductCount,
  ]);

  return (
    <Modal
      appId="root"
      onRequestClose={() => goBack()}
      open
      overlayClose
      variant="standard"
    >
      <ModalHeader nested onRequestClose={() => goBack()}>
        <Flex alignItems="center" grow={1} justifyContent="space-between">
          <Typography mr={20} variant={isIpad ? 'h3-semibold' : 'h2-semibold'}>
            Filter by top terpenes
          </Typography>
          <Flex>
            <Button
              label="Clear all"
              onClick={() => onClearAll()}
              size={isIpad ? 'small' : 'default'}
              variant="tertiary"
            />
            <Button
              loading={isLoading}
              ml={16}
              label={`Show ${pluralize({
                noun: 'product',
                number: filteredProductCount,
              })}`}
              size={isIpad ? 'small' : 'default'}
              onClick={onShowFilteredProducts}
              to={pathname.replace(/\/filters\/compound_names$/, '')}
            />
          </Flex>
        </Flex>
      </ModalHeader>
      <Box background="grays-ultralight" height={16} width="100%" />
      <Modal.Content padding={false}>
        <Flex
          flexDirection="column"
          px={getKioskSpacing(32, 40)}
          py={getKioskSpacing(32, 40)}
        >
          <Flex
            alignItems="center"
            mb={16}
            grow={1}
            justifyContent="space-between"
          >
            <Typography variant="h3-semibold">Top terpenes</Typography>
            <Link onClick={() => onDeselect('compound_names')}>
              <Typography>Deselect all</Typography>
            </Link>
          </Flex>

          <SearchInputContainer>
            <SearchField
              debounceDelay={300}
              defaultValue={terpeneSearchTerm}
              enterKeyHint="search"
              isDebounced
              label="Search top terpenes"
              labelHidden
              mb={getKioskSpacing(16, 24)}
              name="terpene-search"
              onChange={setTerpeneSearchTerm}
              placeholder="Search for a terpene"
            />
          </SearchInputContainer>

          <Flex flexDirection="column" grow={1}>
            {isLoading ? (
              <Flex alignItems="center" height="100%" justifyContent="center">
                <Loading color="black" size="lg" />
              </Flex>
            ) : (
              <>
                {terpenes.length > 0 &&
                  terpenes.map((terpene) => {
                    const checked = isFilterSelected(
                      searchState,
                      'compound_names',
                      terpene
                    );
                    return (
                      <FilterCheckbox
                        checked={checked}
                        item={terpene}
                        key={terpene.value}
                        onChange={(isChecked) => {
                          if (checked !== isChecked) {
                            onChange('compound_names', terpene.value);
                          }
                        }}
                      />
                    );
                  })}
              </>
            )}

            {noResults && (
              <NoResults body="Looks like no terpenes match your search. Please try again." />
            )}
          </Flex>
        </Flex>
      </Modal.Content>
    </Modal>
  );
};
